import { createFileRoute, Link } from '@tanstack/react-router';
import { Bell, Construction, Smartphone, Trophy, Users as UsersIcon } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';

import { LedgerTable, type LedgerEntry } from '@/components/data/LedgerTable';
import { GrantCoinsAction } from '@/features/users/GrantCoinsAction';
import { RequireCapability } from '@/components/layout/RequireCapability';
import { CopyableId } from '@/components/primitives/CopyableId';
import { EntityChip } from '@/components/primitives/EntityChip';
import { LiveDataIndicator } from '@/components/primitives/LiveDataIndicator';
import { StateChip } from '@/components/primitives/StateChip';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { Skeleton } from '@/components/states/Skeleton';
import { StateBlock } from '@/components/states/StateBlock';
import { recordRecentEntity } from '@/features/recents/recent-entities';
import {
  isElevatedUserType,
  USER_TYPE_LABELS,
  useUserDetail,
  type UserDetail,
  type UserDevice,
  type UserSession,
  type UserSquad,
} from '@/features/users/users';

export const Route = createFileRoute('/users/$userId')({
  component: UserDetailScreen,
});

function UserDetailScreen() {
  const { userId } = Route.useParams();
  const { data, isPending, isFetching, isError, error, refetch, dataUpdatedAt } =
    useUserDetail(userId);

  useEffect(() => {
    if (!data) return;
    recordRecentEntity({ kind: 'user', id: data.id, name: data.name, to: `/users/${data.id}` });
  }, [data]);

  if (isError) {
    return (
      <RequireCapability capability="users.view">
        <ErrorState error={error} subject="this user" onRetry={() => void refetch()} />
      </RequireCapability>
    );
  }

  if (isPending) {
    return (
      <RequireCapability capability="users.view">
        <div className="flex flex-col gap-3">
          <Skeleton height={26} width="38%" />
          <Skeleton height={92} />
          <Skeleton height={220} />
        </div>
      </RequireCapability>
    );
  }

  return (
    <RequireCapability capability="users.view">
      <div className="flex flex-col gap-4">
        <IdentityHeader
          user={data}
          isFetching={isFetching}
          dataUpdatedAt={dataUpdatedAt}
          onRefresh={() => void refetch()}
        />
        <TokensPanel user={data} />

        <div className="grid gap-4 lg:grid-cols-2">
          <LeaguesPanel user={data} />
          <SquadsPanel user={data} />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <DevicesPanel devices={data.devices} />
          <SessionsPanel sessions={data.sessions} />
        </div>

        <NotificationsPanel user={data} />
        <AccountActionsPanel />
      </div>
    </RequireCapability>
  );
}

/* ---------------------------------------------------------------- identity */

function IdentityHeader({
  user,
  isFetching,
  dataUpdatedAt,
  onRefresh,
}: {
  user: UserDetail;
  isFetching: boolean;
  dataUpdatedAt: number;
  onRefresh: () => void;
}) {
  return (
    <div className="border-bd bg-card flex flex-col gap-2.5 rounded-xl border p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
        <div className="flex min-w-0 items-center gap-2.5">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt=""
              className="border-bd size-10 flex-none rounded-full border object-cover"
            />
          ) : (
            <span
              aria-hidden
              className="grid size-10 flex-none place-items-center rounded-full font-mono text-[11px] font-bold"
              style={{
                background: 'rgba(var(--info-rgb), 0.2)',
                color: 'var(--info-soft)',
              }}
            >
              US
            </span>
          )}
          <div className="min-w-0">
            <h1 className="font-head text-tx truncate text-[22px] leading-none font-semibold">
              {user.name}
            </h1>
            <div className="text-tx3 mt-1 flex flex-wrap items-center gap-x-2 text-[11.5px]">
              {user.username && <span>@{user.username}</span>}
              {user.profile.school && (
                <>
                  <span aria-hidden>·</span>
                  <span>{user.profile.school}</span>
                </>
              )}
              {user.profile.graduationYear && (
                <>
                  <span aria-hidden>·</span>
                  <span>Class of {String(user.profile.graduationYear)}</span>
                </>
              )}
            </div>
          </div>
        </div>

        <LiveDataIndicator
          updatedAt={dataUpdatedAt || undefined}
          isFetching={isFetching}
          onRefresh={onRefresh}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {user.isRemoved && <StateChip state="deactivated" tone="bad" />}
        {user.isInternal && <StateChip state="internal" tone="live" label="Internal account" />}
        {/*
          Elevated consumer-app privilege, surfaced because it answers "how did this
          person do that?" on a support ticket. Unrelated to this console's roles.
        */}
        {isElevatedUserType(user.userType) && user.userType !== null && (
          <StateChip
            state="elevated"
            tone="warn"
            label={`App ${USER_TYPE_LABELS[user.userType] ?? 'admin'}`}
          />
        )}
        <CopyableId value={user.id} label="User ID" truncate />
      </div>

      <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Email" value={user.email} verified={user.emailVerified} />
        <Field label="Phone" value={user.phone} verified={user.phoneVerified} mono />
        <Field
          label="Last active"
          value={user.lastActiveAt ? user.lastActiveAt.replace('T', ' ').slice(0, 16) : null}
          emptyText="never seen"
          mono
        />
        <Field
          label="Joined"
          value={user.createdAt ? user.createdAt.slice(0, 10) : null}
          mono
        />
      </dl>

      {user.isRemoved && (
        <p
          className="rounded-lg border px-3 py-2 text-[12.5px]"
          style={{
            borderColor: 'rgba(var(--err-rgb), 0.36)',
            background: 'rgba(var(--err-rgb), 0.07)',
            color: 'var(--err-soft)',
          }}
        >
          This account is deactivated
          {user.deactivatedAt ? ` (${user.deactivatedAt.slice(0, 10)})` : ''}. Personal data is
          wiped on self-serve deletion upstream, so blank contact fields here are expected
          rather than missing data.
        </p>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  verified,
  emptyText = '—',
  mono = false,
}: {
  label: string;
  value: string | null;
  verified?: boolean;
  emptyText?: string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt
        className="font-head text-tx3 text-[10px] font-semibold uppercase"
        style={{ letterSpacing: '0.14em' }}
      >
        {label}
      </dt>
      <dd className="mt-0.5 flex min-w-0 items-center gap-1.5">
        <span
          className={mono ? 'mesh-mono truncate text-[12.5px]' : 'truncate text-[13px]'}
          style={{ color: value ? 'var(--tx)' : 'var(--tx3)' }}
        >
          {value ?? emptyText}
        </span>
        {value && verified && (
          <span
            title="Verified"
            className="flex-none text-[10px]"
            style={{ color: 'var(--ok)' }}
          >
            ✓
          </span>
        )}
      </dd>
    </div>
  );
}

/* ------------------------------------------------------------------ tokens */

function TokensPanel({ user }: { user: UserDetail }) {
  const entries: LedgerEntry[] = user.tokens.entries.map((entry) => ({
    id: entry.id,
    occurredAt: entry.occurredAt.replace('T', ' ').slice(0, 19),
    delta: entry.delta,
    // The ledger records the balance at write time. LedgerTable renders it as
    // stored and flags drift; a missing value becomes 0 rather than a guess.
    balance: entry.balanceAfter ?? 0,
    reason: entry.reason,
    sourceType: entry.sourceType,
    externalId: entry.externalId ?? undefined,
  }));

  const ledgerSum = user.tokens.entries.reduce((total, entry) => total + entry.delta, 0);
  // Only meaningful when the whole ledger is loaded — a truncated page sums to less
  // than the balance by definition, which is not drift.
  const balanceDisagrees =
    !user.tokens.ledgerTruncated &&
    user.tokens.balance !== null &&
    user.tokens.balance !== ledgerSum;

  return (
    <Panel
      title="Tokens"
      count={user.tokens.ledgerTotal}
      actions={
        <div className="flex items-center gap-3">
          <span className="mesh-mono text-tx text-[15px]">
            {user.tokens.balance === null ? '—' : user.tokens.balance.toLocaleString()}
            <span className="text-tx3 ml-1 text-[11px]">balance</span>
          </span>
          {/* Beside the balance and above the ledger on purpose: the drift warning below is
              something you must read BEFORE granting, and this puts them on one screen. */}
          <GrantCoinsAction
            userId={user.id}
            username={user.username ?? user.name}
            balance={user.tokens.balance}
          />
        </div>
      }
    >
      {balanceDisagrees && user.tokens.balance !== null && (
        <p
          role="alert"
          className="mb-2.5 rounded-lg border px-3 py-2 text-[12.5px]"
          style={{
            borderColor: 'rgba(var(--warn-rgb), 0.4)',
            background: 'rgba(var(--warn-rgb), 0.1)',
            color: 'var(--warn)',
          }}
        >
          The cached balance on the user record ({user.tokens.balance.toLocaleString()}) does
          not match the sum of the ledger ({ledgerSum.toLocaleString()}). The ledger is the
          source of truth; the balance is a denormalised cache. Investigate before granting or
          debiting anything.
        </p>
      )}

      {user.tokens.ledgerTruncated && (
        <p className="text-tx3 mb-2.5 text-[11.5px]">
          Showing the most recent {user.tokens.entries.length} of{' '}
          {user.tokens.ledgerTotal.toLocaleString()} entries.
        </p>
      )}

      {entries.length === 0 ? (
        <EmptyState
          title="No token activity"
          description="This user has never purchased, earned or spent tokens."
        />
      ) : (
        <LedgerTable entries={entries} unit="tokens" label="token ledger" />
      )}
    </Panel>
  );
}

/* ----------------------------------------------------------------- leagues */

function LeaguesPanel({ user }: { user: UserDetail }) {
  return (
    <Panel title="Leagues" icon={Trophy} count={user.leagues.length}>
      {user.leagues.length === 0 ? (
        <EmptyState title="No leagues" description="This user has not joined a league." />
      ) : (
        <ul className="flex flex-col gap-1.5">
          {user.leagues.map((league) => (
            <li
              key={league.id}
              className="border-bd/60 flex flex-wrap items-center justify-between gap-2 border-b pb-1.5 last:border-b-0 last:pb-0"
            >
              <EntityChip
                kind="league"
                id={league.id}
                name={league.name}
                secondary={league.teamSquad?.name ?? undefined}
                imageUrl={league.imageUrl ?? undefined}
                to={`/leagues/${league.id}`}
              />
              <div className="flex flex-wrap items-center gap-1">
                {league.roles.map((role) => (
                  <StateChip key={role} state={role} size="sm" />
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ------------------------------------------------------------------ squads */

function SquadsPanel({ user }: { user: UserDetail }) {
  // Memberships are flipped to left/removed rather than deleted, so past squads
  // arrive in the same list. Splitting them keeps "is in" separate from "was in".
  const current = user.squads.filter((squad) => squad.status === 'active');
  const past = user.squads.filter((squad) => squad.status !== 'active');

  return (
    <Panel title="Core squads" icon={UsersIcon} count={current.length}>
      {user.squads.length === 0 ? (
        <EmptyState title="No squads" description="This user is not in a core squad." />
      ) : (
        <div className="flex flex-col gap-3">
          <ul className="flex flex-col gap-2">
            {current.map((squad) => (
              <SquadRow key={squad.id} squad={squad} />
            ))}
          </ul>

          {past.length > 0 && (
            <details className="group">
              <summary className="text-tx3 hover:text-tx2 cursor-pointer text-[11.5px]">
                {past.length} former {past.length === 1 ? 'squad' : 'squads'}
              </summary>
              <ul className="mt-2 flex flex-col gap-2 opacity-70">
                {past.map((squad) => (
                  <SquadRow key={squad.id} squad={squad} />
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </Panel>
  );
}

function SquadRow({ squad }: { squad: UserSquad }) {
  return (
    <li className="border-bd/60 flex flex-col gap-1 border-b pb-2 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <EntityChip
          kind="squad"
          id={squad.id}
          name={squad.name}
          imageUrl={squad.imageUrl ?? undefined}
        />
        <div className="flex flex-wrap items-center gap-1">
          {squad.role && <StateChip state={squad.role} size="sm" />}
          {squad.status && squad.status !== 'active' && (
            <StateChip state={squad.status} size="sm" tone="neutral" />
          )}
        </div>
      </div>
      {/*
        Mascots belong to the SQUAD, not to this user — stated inline because the
        obvious reading of a cosmetic on a user page is that they own it, and an
        operator acting on that would be wrong about who they are taking it from.
      */}
      {(squad.activeMascot ?? squad.unlockedMascots.length > 0) && (
        <p className="text-tx3 text-[11px]">
          Squad-owned mascots: {squad.unlockedMascots.length || 'none'}
          {squad.activeMascot ? ` · equipped ${squad.activeMascot}` : ''}
          {squad.canEquipMascot ? ' · this user can equip' : ''}
        </p>
      )}
    </li>
  );
}

/* ------------------------------------------------------- devices, sessions */

function DevicesPanel({ devices }: { devices: UserDevice[] }) {
  const active = devices.filter((device) => device.isActive);

  return (
    <Panel title="Push devices" icon={Smartphone} count={active.length}>
      {devices.length === 0 ? (
        <EmptyState
          title="No registered devices"
          description="This user will not receive push notifications."
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {devices.map((device) => (
            <li
              key={device.id}
              className="border-bd/60 flex flex-wrap items-start justify-between gap-2 border-b pb-2 last:border-b-0 last:pb-0"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-tx text-[12.5px]">
                    {platformLabel(device.platform)}
                  </span>
                  {device.appVersion && (
                    <span className="mesh-mono text-tx3 text-[11px]">v{device.appVersion}</span>
                  )}
                </div>
                <div className="mesh-mono text-tx3 mt-0.5 text-[11px]">
                  last seen {device.lastSeenAt?.replace('T', ' ').slice(0, 16) ?? 'never'}
                </div>
                {/* The usual answer to "why did notifications stop". */}
                {device.disableReason && (
                  <div className="mt-0.5 text-[11px]" style={{ color: 'var(--warn)' }}>
                    disabled: {device.disableReason}
                  </div>
                )}
              </div>
              <StateChip
                state={device.isActive ? 'active' : 'disabled'}
                size="sm"
                tone={device.isActive ? 'good' : 'neutral'}
              />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function SessionsPanel({ sessions }: { sessions: UserSession[] }) {
  const live = sessions.filter((session) => session.isLive);

  return (
    <Panel title="Sessions" count={live.length}>
      {sessions.length === 0 ? (
        <EmptyState title="No sessions" description="This user has never signed in." />
      ) : (
        <ul className="flex flex-col gap-2">
          {sessions.map((session) => (
            <li
              key={session.id}
              className="border-bd/60 flex flex-wrap items-start justify-between gap-2 border-b pb-2 last:border-b-0 last:pb-0"
            >
              <div className="min-w-0">
                <span className="text-tx text-[12.5px]">{platformLabel(session.platform)}</span>
                <div className="mesh-mono text-tx3 mt-0.5 text-[11px]">
                  used {session.lastUsedAt?.replace('T', ' ').slice(0, 16) ?? '—'}
                  {session.expiresAt && ` · expires ${session.expiresAt.slice(0, 10)}`}
                </div>
              </div>
              <StateChip
                state={session.revokedAt ? 'revoked' : session.isLive ? 'active' : 'expired'}
                size="sm"
                tone={session.isLive ? 'good' : 'neutral'}
              />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/* ----------------------------------------------------------- notifications */

function NotificationsPanel({ user }: { user: UserDetail }) {
  const preferences = Object.entries(user.notificationPreferences ?? {});

  return (
    <Panel title="Notification preferences" icon={Bell}>
      {preferences.length === 0 ? (
        <EmptyState
          title="Defaults"
          description="This user has never changed a notification preference, so upstream defaults apply."
        />
      ) : (
        <ul className="flex flex-wrap gap-1.5">
          {preferences.map(([key, enabled]) => (
            <li key={key}>
              <StateChip
                state={enabled ? 'on' : 'off'}
                size="sm"
                tone={enabled ? 'good' : 'neutral'}
                label={`${humanise(key)}: ${enabled ? 'on' : 'off'}`}
              />
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Platform values are stored lowercase upstream ('ios' | 'android' | 'web'), and a
 * CSS `capitalize` renders "ios" as "Ios". These are proper nouns, so they are
 * mapped rather than transformed.
 */
const PLATFORM_LABELS: Record<string, string> = {
  ios: 'iOS',
  android: 'Android',
  web: 'Web',
  unknown: 'Unknown',
};

function platformLabel(platform: string): string {
  return PLATFORM_LABELS[platform] ?? platform;
}

function humanise(key: string): string {
  const spaced = key.replace(/([A-Z])/g, ' $1').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/* --------------------------------------------------------- account actions */

/**
 * Deliberately empty of controls rather than showing disabled ones.
 *
 * Nothing here is a UI gap: user writes have no delegate operation in
 * mesh-admin-api's registry, and ZazzBackend has no admin-callable endpoint for
 * them. Rendering a greyed-out "Deactivate" would teach an operator the button
 * exists and the tool is broken, rather than that the capability does not exist
 * yet — the same reasoning as the ledger having no edit affordance.
 */
function AccountActionsPanel() {
  return (
    <Panel title="Account actions">
      <StateBlock
        icon={Construction}
        tone="info"
        title="Read-only for now"
        description={
          <>
            Deactivation, coin debits, forced sign-out and "view as user" are not wired. They
            need a delegate operation in mesh-admin-api and a matching admin-callable endpoint
            upstream — neither exists yet. Coin GRANTS are wired: the control is in the Tokens
            panel above, owner-only. Corrections are new ledger rows made in ZazzBackend, never
            an edit here. See{' '}
            <Link to="/audit" className="underline">
              Audit
            </Link>{' '}
            for what operators have already done.
          </>
        }
      />
    </Panel>
  );
}

/* ------------------------------------------------------------------- shell */

function Panel({
  title,
  icon: Icon,
  count,
  actions,
  children,
}: {
  title: string;
  icon?: typeof Trophy;
  count?: number;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="border-bd bg-card rounded-xl border p-3.5">
      <div className="mb-2.5 flex flex-wrap items-center justify-between gap-2">
        <h2
          className="font-head text-tx2 flex items-center gap-1.5 text-[11px] font-semibold uppercase"
          style={{ letterSpacing: '0.16em' }}
        >
          {Icon && <Icon size={12} aria-hidden />}
          {title}
          {count !== undefined && (
            <span className="mesh-mono text-tx3 text-[11px]">{count}</span>
          )}
        </h2>
        {actions}
      </div>
      {children}
    </section>
  );
}
